import JsPDF from 'jspdf'
import React, { useEffect, useRef, useState } from 'react'
import { Button, FormGroup, Modal, Radio } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'

import LiveTooltip from 'live/components/ui/tooltip'
import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { RoundedModal } from 'plugin-codaf-risk-ext-bowtie/generic'
import { HorizontalGauge } from 'plugin-codaf-risk-ext-bowtie/generic/HorizontalGauge/HorizontalGauge'
import { IAlarp, IBarrierStatusSpecialStatus } from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmSettings } from 'plugin-codaf-risk-ext-bowtie/types/alarms'
import { transformTSIntoNaturalDate, globalComponentsIndex, capitalize } from 'plugin-codaf-risk-ext-bowtie/utils'

import { useBowtieStateds } from '../../../../../hooks'
import { SpecialStatus } from '../../../../../types/enums'
import { getPercentageOf } from '../../../../../utils'
import { bowtieTheme } from '../../../../../utils/constants'
import NoImg from '../../../../../utils/images/noImg.png'
import { BarrierView, BowtieView } from '../../../../BowtieViews'
import { IBarrierCount, IBarrierPropsCount, IOverviewAsset, IOverviewOperation } from '../../AssetSelector'
import { OperationItem } from './components'
import {
    Card,
    CardOperations,
    CardHeader,
    ExportPDFCardContainer,
    ExportPDFContainer,
    ExportButtonBar,
    ExportPDFLevelTwoContainer
} from './styles'

interface IProps {
    asset: IOverviewAsset
    setSelectedBowtie: React.Dispatch<React.SetStateAction<number>>
    alarpList: IAlarp[]
    setShowAlarmModal: (k: boolean) => void
    setAssetToAlarm: (k: IOverviewAsset) => void
    userData: IAlarmSettings
    barrierStatusList: IBarrierStatusSpecialStatus[]
}

export interface IFormData {
    type: string
}

const formDefault: IFormData = {
    type: 'firstLevel'
}

export function mapJoin(map: Map<number, number>): number {
    return Array.from(map.entries())
        .filter(([key]) => key > -2)
        .reduce((acc, [, crr]) => acc + crr, 0)
}

export const AssetCard = React.forwardRef<HTMLDivElement, IProps>(function AssetCard({
    asset,
    setSelectedBowtie,
    alarpList,
    setShowAlarmModal,
    setAssetToAlarm,
    userData,
    barrierStatusList
}) {
    const [totalBarriers, setTotalBarriers] = useState<Map<number, Map<number, IBarrierCount>>>(new Map())
    const [bowtieViews, setBowtieViews] = useState<BowtieView[]>(null)
    const [emptyAlarms, setEmptyAlarms] = useState<boolean>(true)
    const [hasAlarmBowties, setHasAlarmBowties] = useState<boolean>(false)
    const [showExportModal, setShowExportModal] = useState<boolean>(false)
    const [exportTypeValue, setExportTypeValue] = useState<'firstLevel' | 'secondLevel'>('firstLevel')
    const [enabledButtonModal, setEnabledButtonModal] = useState<boolean>(false)
    const [assetDownloadSelected, setAssetDownloadSelected] = useState<number>()
    const [isDownloading, setIsDownloading] = useState<boolean>(false)

    const [totalAssetBarrierCount, setTotalAssetBarrierCount] = useState({} as IBarrierPropsCount)

    const { handleSubmit, control } = useForm<IFormData>({
        mode: 'all',
        defaultValues: formDefault
    })

    const events = useBowtieStateds(bowtieViews)

    const currentUser: string = authorization.user.username

    const assetCardRef = useRef<HTMLDivElement[]>([])

    const generateFirstLevelPDF = (): void => {
        const report = new JsPDF('portrait', 'pt', 'a4')
        const time = new Date()
        try {
            report
                .html(
                    document.querySelector(`#a4-export-pdf-${!assetDownloadSelected ? '' : assetDownloadSelected}`)
                        .outerHTML
                )
                .then(() => {
                    report.save(`closed-resources ${transformTSIntoNaturalDate(time.getTime())}.pdf`)
                    setShowExportModal(false)
                    setIsDownloading(false)
                })
        } catch (e) {
            console.log(e)
            setIsDownloading(false)
        }
    }

    const generateSecondLevelPDF = (): void => {
        const report = new JsPDF('portrait', 'pt', [(842 * 595.28) / 841.89, 842])
        const time = new Date()

        try {
            report
                .html(
                    document.querySelector(
                        `#a4-export-level-two-pdf-${!assetDownloadSelected ? '' : assetDownloadSelected}`
                    ).outerHTML
                )
                .then(() => {
                    report.save(`open-resources ${transformTSIntoNaturalDate(time.getTime())}.pdf`)
                    setShowExportModal(false)
                    setIsDownloading(false)
                })
        } catch (e) {
            console.log(e)
            setIsDownloading(false)
        }
    }

    const changeExportType = (typeDownload: 'firstLevel' | 'secondLevel'): void => {
        setExportTypeValue(typeDownload)
        setEnabledButtonModal(true)
    }

    const submitExportType = (): void => {
        setIsDownloading(true)
        if (exportTypeValue === 'firstLevel') {
            generateFirstLevelPDF()
        }
        if (exportTypeValue === 'secondLevel') {
            generateSecondLevelPDF()
        }

        setEnabledButtonModal(false)
    }

    const handleCheckIfUserHasAlarmsInAsset = (): Set<BowtieView> => {
        const userAlarmSettings = userData
        const bowties = new Set<BowtieView>()
        const assets: IOverviewAsset[] = []

        userAlarmSettings?.selectedBowties.map(sbt => {
            asset.operations.map(op =>
                op.bowtieViews.map(bt => {
                    if (bt.bowtie.id === sbt.bowtieId) {
                        if (assets.indexOf(asset) === -1) {
                            assets.push(asset)
                        }
                        setHasAlarmBowties(true)
                    }
                })
            )
        })

        return bowties
    }

    function getStatusCountFromBarriers(barriersView: BarrierView[]): Map<number, number> {
        const returnCountMap = new Map<number, number>()
        barriersView.map(barrierView => {
            if (barrierView.event != null) {
                if (barrierView.event.status === SpecialStatus.UNMONITORED) {
                    returnCountMap.set(-2, (returnCountMap.get(-2) ?? 0) + 1)
                } else {
                    const barrierStatusID = Number(barrierView.event.status_id)
                    returnCountMap.set(barrierStatusID, (returnCountMap.get(barrierStatusID) ?? 0) + 1)
                }
            } else {
                returnCountMap.set(-1, (returnCountMap.get(-1) ?? 0) + 1)
            }

            if (barrierView.escalationFactorsView != null && barrierView.escalationFactorsView.length > 0) {
                barrierView.escalationFactorsView.map(escalationView => {
                    const controlBarrierResult = getStatusCountFromBarriers(escalationView.barriersView)
                    controlBarrierResult.forEach((qtt, statusID) => {
                        returnCountMap.set(statusID, (returnCountMap.get(statusID) ?? 0) + qtt)
                    })
                })
            }
        })

        return returnCountMap
    }

    function getBarrierStatusCountFromBowties(bowtieView: BowtieView): IBarrierCount {
        const returnCountObject: IBarrierCount = {
            prevention: new Map(),
            mitigation: new Map()
        }
        bowtieView.topEventView.threatsView.map(threatView => {
            getStatusCountFromBarriers(threatView.barriersView)
            const controlBarrierResult = getStatusCountFromBarriers(threatView.barriersView)
            controlBarrierResult.forEach((qtt, statusID) => {
                returnCountObject.prevention.set(
                    statusID,
                    (returnCountObject.prevention.get(statusID) ?? 0) + (qtt ?? 0)
                )
            })
        })

        bowtieView.topEventView.consequencesView.map(consequenceView => {
            getStatusCountFromBarriers(consequenceView.barriersView)
            const controlBarrierResult = getStatusCountFromBarriers(consequenceView.barriersView)
            controlBarrierResult.forEach((qtt, statusID) => {
                returnCountObject.mitigation.set(
                    statusID,
                    (returnCountObject.mitigation.get(statusID) ?? 0) + (qtt ?? 0)
                )
            })
        })

        return returnCountObject
    }

    function getTotalBarriersFromAnOperationBuilder(operation: IOverviewOperation): Map<number, IBarrierCount> {
        const count = new Map<number, IBarrierCount>()
        operation.bowtieViews.forEach(bwt => count.set(bwt.bowtie.id, getBarrierStatusCountFromBowties(bwt)))

        return count
    }

    function getTotalBarriersCountBuilder(): void {
        const count = new Map<number, Map<number, IBarrierCount>>()
        asset.operations.forEach(op => count.set(op.id, getTotalBarriersFromAnOperationBuilder(op)))
        setTotalBarriers(count)
    }

    function getTotalAssetBarrierCount(): void {
        const items: IBarrierPropsCount = {
            total: 0,
            unknown: 0,
            unmonitored: 0
        }
        totalBarriers?.forEach(operationCount =>
            operationCount.forEach(bowtieCount => {
                items.total = items.total + mapJoin(bowtieCount.prevention) + mapJoin(bowtieCount.mitigation)
                items.unknown =
                    items.unknown + (bowtieCount.prevention.get(-1) ?? 0) + (bowtieCount.mitigation.get(-1) ?? 0)
                items.unmonitored =
                    items.unmonitored + (bowtieCount.prevention.get(-2) ?? 0) + (bowtieCount.mitigation.get(-2) ?? 0)
                const keys = Array.from(
                    new Set<number>([...bowtieCount.prevention.keys(), ...bowtieCount.mitigation.keys()])
                ).filter(e => e > 0)
                keys.forEach(key => {
                    items[key] =
                        (items[key] ?? 0) +
                        (bowtieCount.prevention.get(key) ?? 0) +
                        (bowtieCount.mitigation.get(key) ?? 0)
                })
            })
        )

        setTotalAssetBarrierCount(items)
    }

    const getAssetDownloadSelected = (): string => {
        return assetCardRef?.current[assetDownloadSelected]?.innerHTML
    }

    const exportPdfButton = (assetId: number): void => {
        setShowExportModal(!showExportModal)
        setAssetDownloadSelected(assetId)
    }

    useEffect(() => {
        if (asset && events) {
            getTotalBarriersCountBuilder()
        }
    }, [events]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (asset) {
            getTotalBarriersCountBuilder()
            setBowtieViews(asset.operations.flatMap(op => op.bowtieViews))
        }
        handleCheckIfUserHasAlarmsInAsset()
    }, [asset]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            globalComponentsIndex.reset()
        }
    }, [])

    useEffect(() => {
        if (totalBarriers) {
            getTotalAssetBarrierCount()
        }
    }, [totalBarriers]) // eslint-disable-line react-hooks/exhaustive-deps

    const wrapperValidatePercentage = (countStatus: number, totalNumber: number): number | string => {
        const percentageOf = getPercentageOf(countStatus, totalNumber)

        if (Number.isNaN(percentageOf)) return '--'

        return Math.round(percentageOf)
    }

    return (
        <>
            <Card
                style={{
                    filter:
                        asset.overviewAlarms.filter(alarm => !alarm.seen_by).length > 0
                            ? asset.overviewAlarms.filter(alarm => !alarm.seen_by && alarm.entity === 'TOP_EVENT')
                                  .length > 0
                                ? 'drop-shadow(2px 2px 6px red)'
                                : 'drop-shadow(2px 2px 6px #F8CC2D)'
                            : ''
                }}
                id={`asset-card-pdf-export ${asset.id}`}
                ref={e => (assetCardRef.current[asset.id] = e)}
            >
                <CardHeader emptyAlarms={emptyAlarms} className="export-pdf card-header">
                    <div>
                        <LiveTooltip content={i18n('Export data')} position="top">
                            <ExportButtonBar className="export-pdf button-download">
                                <Button
                                    onClick={() => {
                                        exportPdfButton(asset.id)
                                    }}
                                    className="button-svg"
                                >
                                    <i className="fas fa-ellipsis-v" />
                                </Button>
                            </ExportButtonBar>
                        </LiveTooltip>
                    </div>

                    <div className="export-pdf card-details" id={'overview-resourceCardHeader-div-cardDetails'}>
                        <div className="export-pdf card-title">
                            <div className="card-title-text">
                                <div className="card-title-button">{asset.name ?? 'RESOURCE_NAME'}</div>
                                <div className="card-asset-id">{asset.assetCode ?? 'CODE'}</div>
                            </div>
                        </div>
                        {hasAlarmBowties && (
                            <div className="export-pdf card-alarms">
                                {emptyAlarms ? (
                                    <i className="export-pdf far fa-bell" />
                                ) : (
                                    <i className="export-pdf fas fa-bell" />
                                )}

                                <div className="card-alarms-text unseen">
                                    {asset?.overviewAlarms.filter(alarm => !alarm.seen_by).length}{' '}
                                    {i18n(`unseen alarms`)}
                                </div>
                                <div
                                    className="export-pdf card-alarms-text view"
                                    onClick={() => {
                                        setShowAlarmModal(true)
                                        setAssetToAlarm(asset)
                                    }}
                                >
                                    {i18n('view')}
                                </div>
                            </div>
                        )}

                        <div className="card-status">{(asset.status ?? '').toUpperCase()}</div>
                        <div className="card-spacer" />
                        <div
                            className="export-pdf card-degraded-percentage"
                            id={'overview-resourceCardHeader-div-degradedPercentage'}
                        >
                            {barrierStatusList
                                ?.filter(bs => bs.specialStatus !== SpecialStatus.UNMONITORED)
                                .map(bs => {
                                    const currentBarrierCount = totalAssetBarrierCount?.[bs.id] ?? 0
                                    const totalBarrierCount = totalAssetBarrierCount?.total ?? 0

                                    return (
                                        <div className="export-pdf status-row unknown" key={bs.id}>
                                            <div className="export-pdf gauge-div">
                                                <HorizontalGauge
                                                    actual={currentBarrierCount}
                                                    total={totalBarrierCount}
                                                    color={bs.colorReference}
                                                />
                                            </div>
                                            <div className="status-col align-left">
                                                <div className="export-pdf norule-row">
                                                    <span style={{ marginRight: 2, color: bs.colorReference }}>
                                                        {wrapperValidatePercentage(
                                                            currentBarrierCount,
                                                            totalBarrierCount
                                                        )}{' '}
                                                    </span>
                                                    % {capitalize(bs.statusCustomName ?? bs.name)}
                                                </div>
                                            </div>
                                            <div className="status-col align-right">
                                                <div className="export-pdf norule-row">
                                                    <span style={{ marginRight: 2, color: bs.colorReference }}>
                                                        {currentBarrierCount}
                                                    </span>
                                                    / {totalBarrierCount} {i18n('barriers')}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            <div className="export-pdf status-row unknown">
                                <div className="export-pdf gauge-div">
                                    <HorizontalGauge
                                        actual={totalAssetBarrierCount.unknown}
                                        total={totalAssetBarrierCount.total}
                                        color={bowtieTheme.barrierColors.unknown}
                                    />
                                </div>
                                <div className="status-col align-left">
                                    <div className="export-pdf norule-row">
                                        <span style={{ marginRight: 2, color: bowtieTheme.barrierColors.unknown }}>
                                            {wrapperValidatePercentage(
                                                totalAssetBarrierCount.unknown,
                                                totalAssetBarrierCount.total
                                            )}{' '}
                                        </span>
                                        % {capitalize(SpecialStatus.UNKNOWN)}
                                    </div>
                                </div>
                                <div className="status-col align-right">
                                    <div className="export-pdf norule-row">
                                        <span style={{ marginRight: 2, color: bowtieTheme.barrierColors.unknown }}>
                                            {totalAssetBarrierCount.unknown}
                                        </span>
                                        / {totalAssetBarrierCount.total} {i18n('barriers')}
                                    </div>
                                </div>
                            </div>
                            <div className="export-pdf status-row unmonitored align-left">
                                <div className="export-pdf gauge-div" />
                                <div className="status-col">
                                    --{' '}
                                    {capitalize(
                                        barrierStatusList?.find(bs => bs.specialStatus === SpecialStatus.UNMONITORED)
                                            ?.statusCustomName ?? SpecialStatus.UNMONITORED
                                    )}
                                </div>
                                <div className="status-col align-right">
                                    <div className="export-pdf norule-row">
                                        <span style={{ marginRight: 2, color: bowtieTheme.barrierColors.unmonitored }}>
                                            {totalAssetBarrierCount.unmonitored}
                                        </span>{' '}
                                        {i18n('barriers')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="export-pdf card-side">
                        <div className="card-asset-thumbnail" id={'overview-resourceCardHeader-div-thumbnail'}>
                            <img src={asset.avatar || NoImg} id={'overview-resourceCardHeader-img-thumbnail'} />
                        </div>
                        <div className="export-pdf card-supervisor">
                            <div className="export-pdf card-label" id={'overview-resourceCardHeader-div-managerLabel'}>
                                {i18n('MANAGER')}
                            </div>
                            <LiveTooltip position="top" content={asset.responsiblePerson ?? 'RESOURCE_MANAGER'}>
                                <div
                                    className="export-pdf card-item"
                                    id={'overview-resourceCardHeader-div-responsiblePerson'}
                                >
                                    {asset.responsiblePerson ?? 'RESOURCE_MANAGER'}
                                </div>
                            </LiveTooltip>
                        </div>
                    </div>
                </CardHeader>
                <CardOperations
                    operations={asset.operations.length}
                    id={'overview-resourceCardBody-div-operations'}
                    className="export-pdf card-operations"
                >
                    {barrierStatusList != null &&
                        barrierStatusList.length > 0 &&
                        totalBarriers &&
                        asset.operations
                            .sort((a, b) => {
                                if (a.description < b.description) {
                                    return -1
                                }

                                if (a.description > b.description) {
                                    return 1
                                }

                                return 0
                            })
                            .map((operation, i) => (
                                <OperationItem
                                    id={`overview-resourceCardBody-div-operationItem${i}`}
                                    inactiveStatus={barrierStatusList.find(
                                        bs => bs.specialStatus === SpecialStatus.INACTIVE
                                    )}
                                    activeStatus={barrierStatusList.find(
                                        bs => bs.specialStatus === SpecialStatus.ACTIVE
                                    )}
                                    key={i}
                                    operation={operation}
                                    setSelectedBowtie={setSelectedBowtie}
                                    operationStatusCount={totalBarriers.get(operation.id)}
                                    alarpList={alarpList}
                                />
                            ))}
                </CardOperations>
            </Card>
            <div style={{ display: 'none' }}>
                <ExportPDFContainer id={`a4-export-pdf-${!assetDownloadSelected ? '' : assetDownloadSelected}`}>
                    <ExportPDFCardContainer
                        key={assetCardRef?.current[assetDownloadSelected]?.id}
                        dangerouslySetInnerHTML={{ __html: getAssetDownloadSelected() }}
                    />
                </ExportPDFContainer>
                <ExportPDFLevelTwoContainer
                    id={`a4-export-level-two-pdf-${!assetDownloadSelected ? '' : assetDownloadSelected}`}
                >
                    <ExportPDFCardContainer
                        key={assetCardRef?.current[assetDownloadSelected]?.id}
                        dangerouslySetInnerHTML={{ __html: getAssetDownloadSelected() }}
                    />
                </ExportPDFLevelTwoContainer>
                <RoundedModal
                    show={showExportModal}
                    onHide={() => setShowExportModal(false)}
                    aria-labelledby="contained-modal-title"
                >
                    <form onSubmit={handleSubmit(submitExportType)}>
                        <Modal.Header closeButton>
                            <Modal.Title id="contained-modal-title">
                                <i className="fas fa-download" /> {i18n('Export data')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Controller
                                rules={{ required: true }}
                                name="type"
                                control={control}
                                render={({ fieldState }) => (
                                    <FormGroup
                                        validationState={fieldState.error ? 'error' : 'success'}
                                        className="pdf-export-modal-text
                                    "
                                    >
                                        <label className="control-label" style={{ marginBottom: 8 }}>
                                            {i18n('Choose how you want to export this cards:')}
                                        </label>
                                        <Radio
                                            name="radioGroup"
                                            onChange={() => changeExportType('firstLevel')}
                                            value="firstLevel"
                                            style={{ marginBottom: 8 }}
                                        >
                                            {i18n('Closed card')}
                                        </Radio>
                                        <Radio
                                            name="radioGroup"
                                            onChange={() => changeExportType('secondLevel')}
                                            value="secondLevel"
                                        >
                                            {i18n('Open card')}
                                        </Radio>
                                    </FormGroup>
                                )}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                className="no-outline-button"
                                bsStyle="primary"
                                type="submit"
                                disabled={!enabledButtonModal}
                            >
                                {isDownloading ? <i className="fa fa-spinner fa-spin" /> : i18n('Confirm export')}
                            </Button>
                            <Button
                                className="no-outline-button"
                                type="button"
                                onClick={() => setShowExportModal(false)}
                            >
                                {i18n('Cancel')}
                            </Button>
                        </Modal.Footer>
                    </form>
                </RoundedModal>
            </div>
        </>
    )
})
